import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Box,
    Stack,
    FormControl,
    FormLabel,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { axios } from '@/services/axios';
import {
    Button,
    Heading,
    Input,
    LoadingIndicator,
    Text,
} from '@/components/v4';
import FieldToolTip from '@/components/forms/new/FieldToolTip';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
    textBoxStyling,
    breadCrumbStyling,
    headingStyling,
} from '@/components/forms/new/formStyles';
import { statusToString } from '../utils';

const EditZoomTrunk = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [trunkName, setTrunkName] = useState('');
    const [trunkData, setTrunkData] = useState(null);
    const [isHovered, setIsHovered] = useState(false);

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);

    const handleBackbutton = () => {
        navigate('/trunks');
        dispatch({
            type: 'CHANGE_TABLE_LOADING',
        });
    };

    // loading trunk api call
    useEffect(() => {
        if (id) {
            axios
                .get(`/Trunk/${id}`)
                .then((res) => {
                    setTrunkData(res.data);
                    setTrunkName(res.data.name || '');
                    setIsLoading(false);
                })
                .catch((err) => {
                    toast.error(
                        err?.response?.data?.message || 'Failed to load trunk',
                    );
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // editing a trunk api call
    const handleEditTrunk = (e) => {
        e.preventDefault();
        setIsLoading(true);
        axios
            .post(
                '/trunk/name',
                {
                    trunkId: trunkData.id,
                    trunkName: trunkName,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            )
            .then(() => {
                toast.success('Trunk updated successfully');
                navigate('/trunks');
                dispatch({
                    type: 'CHANGE_TABLE_LOADING',
                });
            })
            .catch((err) => {
                toast.error(
                    err?.response?.data?.message || 'Failed to update trunk',
                );
                setIsLoading(false);
            });
    };

    // reset navigation on company change
    if (trunkData?.companyID !== currentCompany) {
        navigate('/trunks');
    }

    const breadCrumbTrail = [
        { label: 'Trunks', path: '/trunks' },
        { label: 'Edit Trunk', path: '' },
    ];

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                crumb.path === '/trunks'
                                    ? () =>
                                          dispatch({
                                              type: 'CHANGE_TABLE_LOADING',
                                          })
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>

            <Heading sx={headingStyling}>
                {trunkData?.sipHeader || trunkData?.name}
            </Heading>

            <Box as="form" onSubmit={handleEditTrunk}>
                <Box
                    borderRadius={15}
                    padding="30px"
                    maxWidth={895}
                    ml={0}
                    bg={darkMode ? 'dark.tcap' : 'white'}
                    border="1px solid"
                    borderColor={
                        darkMode ? 'dark.tcap-borderGray' : 'gray.200'
                    }>
                    {trunkData?.status !== undefined && (
                        <FormControl sx={{ margin: '0 !important', mb: 4 }}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling()}>
                                <FormLabel
                                    sx={formLabelStyling(darkMode, true)}>
                                    Status
                                </FormLabel>
                                <Box flex={1} maxWidth="400px">
                                    <Text>
                                        {statusToString(trunkData.status)}
                                    </Text>
                                </Box>
                            </Stack>
                        </FormControl>
                    )}

                    <FormControl
                        sx={{ margin: '0 !important', mb: 4 }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}>
                        <Stack
                            direction="row"
                            spacing={stackSpacing}
                            sx={{ ...stackStyling(), height: 'auto' }}>
                            <FormLabel sx={formLabelStyling(darkMode, true)}>
                                Trunk Name
                            </FormLabel>
                            <Box flex={1} maxWidth="400px">
                                <Input
                                    type="text"
                                    id="trunkName"
                                    name="trunkName"
                                    value={trunkName}
                                    onChange={(e) =>
                                        setTrunkName(e.target.value)
                                    }
                                    sx={textBoxStyling}
                                    placeholder="Enter trunk name"
                                />
                            </Box>
                            {isHovered && (
                                <FieldToolTip
                                    label="The display name for this trunk"
                                    ariaLabel="trunk-name-tip"
                                    left="25px"
                                    top="5px"
                                />
                            )}
                        </Stack>
                    </FormControl>
                </Box>

                <Box sx={{ marginTop: '25px', display: 'flex', gap: 2 }}>
                    <Button
                        variant="outline"
                        sx={{ background: 'white', minWidth: '120px' }}
                        onClick={handleBackbutton}>
                        Back
                    </Button>
                    <Button
                        type="submit"
                        sx={{ minWidth: '120px' }}
                        isLoading={isLoading}>
                        Update
                    </Button>
                </Box>
            </Box>
        </>
    );
};

export default EditZoomTrunk;
